/**
 * Network Service - Web
 *
 * Abstrae la detección de online/offline para web.
 * Usa navigator.onLine.
 *
 * USO:
 * import { networkService } from './services/network/networkService';
 *
 * if (networkService.isOnline()) { ... }
 * const unsubscribe = networkService.onChange((online) => console.log('Online:', online));
 */

/**
 * Network Service - Abstracción de conectividad de red (Web)
 */
export const networkService = {
  /**
   * Verifica si el dispositivo está online
   * Web: navigator.onLine
   */
  isOnline(): boolean {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  },

  /**
   * Suscribe a cambios de conectividad
   * Web: window.addEventListener('online', 'offline')
   *
   * @param callback - Función llamada cuando cambia el estado (online: boolean)
   * @returns Función para desuscribir
   */
  onChange(callback: (online: boolean) => void): () => void {
    const handleOnline = () => callback(true);
    const handleOffline = () => callback(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Call immediately with current state
    callback(this.isOnline());

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  },

  /**
   * Espera a que el dispositivo vuelva a estar online
   * Útil para sincronización offline-first
   *
   * @param timeout - Tiempo máximo de espera (ms). Default: 60000 (1 minuto)
   * @returns Promise que se resuelve cuando vuelve online o rechaza por timeout
   */
  async waitForOnline(timeout: number = 60000): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isOnline()) {
        resolve(true);
        return;
      }

      const timeoutId = setTimeout(() => {
        unsubscribe();
        resolve(false); // Timeout
      }, timeout);

      const unsubscribe = this.onChange((online) => {
        if (online) {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(true);
        }
      });
    });
  },

  /**
   * Obtiene información básica de la red
   */
  async getNetworkInfo(): Promise<{ isConnected: boolean; isInternetReachable: boolean | null }> {
    return {
      isConnected: this.isOnline(),
      isInternetReachable: null // Web no puede distinguir entre "conectado a red" y "con internet"
    };
  }
};
